import { createStoreWithProducer } from '@xstate/store';
import { create } from 'mutative';

import { type DateRangeOption } from '~/ui/molecules/DateInput';

import { isValidManualFilterDate } from './manualDateRange';
import { toPredefinedDateRangeOption } from './predefinedDateRange';
import {
  type BuiltInUniversalFilter,
  type SavedFilterBaseline,
  type UniversalFiltersStoreContext,
} from './store.types';
import {
  type FilterClause,
  type FilterDraft,
  type FilterDraftValue,
  UNIVERSAL_FILTER_ALL_ID,
  UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
  UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
  UNIVERSAL_FILTER_SEARCH_FIELD,
} from './types';

export const DEFAULT_PRIMARY_PREDEFINED_DATE_RANGE = '90-days';

const builtInAllFilter = {
  id: UNIVERSAL_FILTER_ALL_ID,
  isPersistable: false,
  label: 'All',
} satisfies BuiltInUniversalFilter;

type CreateUniversalFiltersStoreOptions = {
  readonly defaultFilterClauses?: FilterDraft;
  readonly initialContext?: UniversalFiltersStoreContext;
  readonly primaryDateDefaultPredefinedRange?: DateRangeOption;
};

/**
 * Creates one universal-filter store with a frontend-only built-in All
 * baseline. Page defaults seed All but are not merged into backend filters.
 */
export function createUniversalFiltersStore({
  defaultFilterClauses,
  initialContext,
  primaryDateDefaultPredefinedRange,
}: CreateUniversalFiltersStoreOptions = {}) {
  const dateClauses = primaryDateDefaultPredefinedRange
    ? getDefaultPrimaryDateDraft(primaryDateDefaultPredefinedRange)
    : [];
  const builtInAllBaseline: FilterDraft = [
    ...dateClauses,
    ...(defaultFilterClauses ?? []),
  ];

  return createStoreWithProducer(create, {
    context: initialContext ?? getInitialContext(builtInAllBaseline),
    on: {
      // Edits affect the single active draft only. Unknown clauses are kept by
      // replacing just the clause with the matching backend field.
      editSelectedDraftClause(
        context,
        event: {
          field: string;
          value?: FilterDraftValue;
        },
      ) {
        context.draft = setClause(context.draft, event);
        context.isDirty = isDirty(context.draft, context.baseline);
      },
      // A row that owns several keys keeps one clause: the new clause takes the
      // place of the row's first one, and any other clause it owns is dropped.
      editSelectedDraftRow(
        context,
        event: { clause: FilterClause; fields: readonly string[] },
      ) {
        const { clause, fields } = event;
        const index = context.draft.findIndex(({ field }) =>
          fields.includes(field),
        );
        context.draft = context.draft.flatMap((current, position) => {
          if (position === index) return [clause];
          return fields.includes(current.field) ? [] : [current];
        });
        context.isDirty = isDirty(context.draft, context.baseline);
      },
      removeSelectedDraftClause(context, event: { field: string }) {
        context.draft = removeClause(context.draft, event.field);
        context.isDirty = isDirty(context.draft, context.baseline);
      },
      // Reset is local-only and restores the selected filter's last known
      // backend baseline, including clauses this UI does not understand.
      resetSelectedDraft(context) {
        context.draft = context.baseline;
        context.isDirty = false;
      },
      // React to TanStack Query data changes without owning the query state.
      // The saved-filter list itself stays in the query cache.
      savedFiltersChanged(
        context,
        event: {
          savedFilters: readonly SavedFilterBaseline[];
        },
      ) {
        applySavedFiltersChanged(context, {
          builtInAllBaseline,
          savedFilters: event.savedFilters,
        });
      },
      // Save responses refresh the active draft. TanStack Query owns mutation
      // status and saved-filter list refetching.
      saveSelectedFilterSucceeded(
        context,
        event: {
          savedFilter: SavedFilterBaseline;
        },
      ) {
        const { savedFilter } = event;
        const sanitizedFilters = sanitizeHydratedClauses(savedFilter.filters);

        context.selectedFilterId = savedFilter.id;
        context.baseline = sanitizedFilters;
        context.draft = sanitizedFilters;
        context.isDirty = false;
      },
      // Switching filters activates that filter immediately. There are no
      // per-filter draft slots, so unsaved edits on the previous selection drop.
      selectFilter(
        context,
        event: {
          baseline: FilterDraft;
          filterId: string;
        },
      ) {
        context.selectedFilterId = event.filterId;
        context.baseline =
          event.filterId === UNIVERSAL_FILTER_ALL_ID
            ? builtInAllBaseline
            : sanitizeHydratedClauses(event.baseline);
        context.draft = context.baseline;
        context.isDirty = false;
      },
      // Parallel map: counts are query-derived UI metadata, not saved-filter data.
      setFilterResultCount(
        context,
        event: {
          count: number;
          filterId: string;
        },
      ) {
        context.resultCountsByFilterId = {
          ...context.resultCountsByFilterId,
          [event.filterId]: event.count,
        };
      },
      setSearch(context, event: { search: string }) {
        context.draft = event.search.trim()
          ? setClause(context.draft, {
              field: UNIVERSAL_FILTER_SEARCH_FIELD,
              value: event.search,
            })
          : removeClause(context.draft, UNIVERSAL_FILTER_SEARCH_FIELD);
        context.isDirty = isDirty(context.draft, context.baseline);
      },
    },
  });
}

function applySavedFiltersChanged(
  context: UniversalFiltersStoreContext,
  {
    builtInAllBaseline,
    savedFilters,
  }: {
    readonly builtInAllBaseline: FilterDraft;
    readonly savedFilters: readonly SavedFilterBaseline[];
  },
) {
  const nextSavedFilterIds = new Set(savedFilters.map((filter) => filter.id));

  // If the selected saved filter disappeared remotely, there is no backend
  // baseline left to compare or save against, so fall back to All.
  if (
    context.selectedFilterId !== UNIVERSAL_FILTER_ALL_ID &&
    !nextSavedFilterIds.has(context.selectedFilterId)
  ) {
    context.selectedFilterId = UNIVERSAL_FILTER_ALL_ID;
    context.baseline = builtInAllBaseline;
    context.draft = builtInAllBaseline;
    context.isDirty = false;
    context.resultCountsByFilterId = pruneResultCountsByValidSavedIds(
      context.resultCountsByFilterId,
      nextSavedFilterIds,
    );
    return;
  }

  const selectedBaseline = getBaseline(
    savedFilters,
    context.selectedFilterId,
    builtInAllBaseline,
  );

  // Refetches may replace the selected filter's baseline. Clean drafts follow
  // the backend; dirty drafts stay user-owned until save, reset, or selection.
  context.baseline = selectedBaseline;

  if (!context.isDirty) {
    context.draft = selectedBaseline;
  }

  context.isDirty = isDirty(context.draft, context.baseline);
  context.resultCountsByFilterId = pruneResultCountsByValidSavedIds(
    context.resultCountsByFilterId,
    nextSavedFilterIds,
  );
}

function getDefaultPrimaryDateDraft(
  defaultPredefinedRange: DateRangeOption,
): FilterDraft {
  return [
    {
      field: UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
      value: defaultPredefinedRange,
    },
  ];
}

function getInitialContext(
  builtInAllBaseline: FilterDraft,
): UniversalFiltersStoreContext {
  return {
    baseline: builtInAllBaseline,
    builtInFilters: [builtInAllFilter],
    draft: builtInAllBaseline,
    isDirty: false,
    resultCountsByFilterId: {},
    selectedFilterId: UNIVERSAL_FILTER_ALL_ID,
  };
}

/**
 * Prunes result counts for saved filters that are no longer in the list.
 */
function pruneResultCountsByValidSavedIds(
  counts: Readonly<Record<string, number>>,
  validSavedFilterIds: ReadonlySet<string>,
): Readonly<Record<string, number>> {
  const next: Record<string, number> = { ...counts };

  for (const key of Object.keys(next)) {
    if (key !== UNIVERSAL_FILTER_ALL_ID && !validSavedFilterIds.has(key)) {
      delete next[key];
    }
  }

  return next;
}

/**
 * Mirrors the primary-date write-path validation in `usePrimaryDateFilter`:
 * a manual range must be a two-element tuple of real, ordered `YYYY-MM-DD`
 * calendar dates inside the supported window.
 */
function isValidPrimaryDateRangeValue(value: FilterClause['value']): boolean {
  if (!Array.isArray(value) || value.length !== 2) return false;

  const [from, to] = value;

  return (
    isValidManualFilterDate(from) && isValidManualFilterDate(to) && from <= to
  );
}

/**
 * Drops hydrated reserved primary-date clauses the UI could never have
 * produced, so the draft stays renderable by the controls and safe for the
 * page mappers.
 *
 * Saved filters are generic backend data: the write path validates these
 * clauses (`usePrimaryDateFilter`) but hydration used to copy them
 * into the draft unchecked. Only the two reserved primary-date fields carry
 * semantic invariants this page-agnostic store can check — generic clause
 * structure is already enforced by `zodSavedFilterClauseSchema` at the fetch
 * boundary, and unknown backend fields are deliberately preserved.
 *
 * The two reserved fields are also mutually exclusive: the write path removes
 * one when it sets the other, so a draft holding both is another state the UI
 * could not have produced. Page mappers assign both to the same query params
 * and would resolve the conflict by clause order, so hydration keeps whichever
 * the date control itself would display.
 */
function sanitizeHydratedClauses(filters: FilterDraft): FilterDraft {
  const validClauses = filters.filter((clause) => {
    if (clause.field === UNIVERSAL_FILTER_PRIMARY_DATE_FIELD) {
      return isValidPrimaryDateRangeValue(clause.value);
    }

    if (clause.field === UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD) {
      return toPredefinedDateRangeOption(clause.value) !== undefined;
    }

    return true;
  });

  const hasPredefinedRange = validClauses.some(
    (clause) =>
      clause.field === UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
  );

  if (!hasPredefinedRange) return validClauses;

  return validClauses.filter(
    (clause) => clause.field !== UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
  );
}

/**
 * Adds or replaces one backend-shaped clause without touching any other clause.
 * This is what preserves dynamic or unknown backend fields during UI edits.
 */
function setClause(
  draft: FilterDraft,
  clause: {
    field: string;
    value?: FilterDraftValue;
  },
): FilterDraft {
  const nextClause =
    'value' in clause
      ? {
          field: clause.field,
          value: clause.value,
        }
      : {
          field: clause.field,
        };

  const clauseIndex = draft.findIndex((item) => item.field === clause.field);

  if (clauseIndex === -1) {
    return [...draft, nextClause];
  }

  return draft.map((item, index) =>
    index === clauseIndex ? nextClause : item,
  );
}

function removeClause(draft: FilterDraft, field: string): FilterDraft {
  return draft.filter((clause) => clause.field !== field);
}

/**
 * Returns the selected filter's saved clauses. Built-in All can have a local
 * baseline from page defaults, but remains frontend-only and non-persistable.
 * Backend clauses pass through hydration sanitization.
 */
function getBaseline(
  savedFilters: readonly SavedFilterBaseline[],
  filterId: string,
  builtInAllBaseline: FilterDraft,
) {
  if (filterId === UNIVERSAL_FILTER_ALL_ID) {
    return builtInAllBaseline;
  }

  return sanitizeHydratedClauses(
    savedFilters.find((filter) => filter.id === filterId)?.filters ?? [],
  );
}

/**
 * Dirty state is a structural comparison of backend-shaped drafts. The store
 * deliberately does not interpret fields or values here.
 */
function isDirty(draft: FilterDraft, baseline: FilterDraft) {
  return JSON.stringify(draft) !== JSON.stringify(baseline);
}

/**
 * Returns the primary-row search text held in the draft's reserved clause.
 */
export function getDraftSearch(draft: FilterDraft): string {
  const value = draft.find(
    (clause) => clause.field === UNIVERSAL_FILTER_SEARCH_FIELD,
  )?.value;

  return typeof value === 'string' ? value : '';
}
